import React, { useState, useEffect, Fragment, useRef, useContext, useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import styled, { keyframes } from 'styled-components'
import { bounceIn } from 'react-animations'
import { ModalFromBottom, Form, QuantitySwitch, Icon, LabelBox } from 'components/base-ui'
import { Button as BaseButton, Fonts, Label, Container } from 'components/base-ui'
import ModalProductModifiersSkeleton from 'components/Skeleton/ModalProductModifiersSkeleton'
import CheckBoxPromotionProduct from 'components/CheckBoxPromotionProduct'

import colors from 'styles/colors'
import spacing from 'styles/spacing'

import { LANGUAGES } from 'helpers/language'
import { displayPrice, displayPriceBothNoneAnd2Digit } from 'helpers'
import {
  getProductInStockQuantity,
  getProductLimitQuantityMode,
  isValidProductMaxQuantityToAddToCart,
  getProductQuantityCanAddToCart,
  isShowLabelExcludedFromProtmoion,
  getRewardTotalSatangs,
} from 'helpers/product'
import { ProductLimitQuantityModeEnum } from 'enums/product'
import { MODE_MODALCUSTOMERNOTES, isSameModifiers } from 'helpers/cart'
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock'

import { Element, scroller } from 'react-scroll'

import { AppContext } from 'contexts/AppProvider'

import { InvalidModifierEnum } from 'enums'
import { ThemeColor } from 'types/color'
import media from 'styles/media'
import { useTranslation } from 'react-i18next'
import * as Sentry from '@sentry/browser'
import { isDevelopment } from 'variables/environment'
import { cloneObject } from 'helpers'
import useLanguage from 'hooks/useLanguage'

const { screenXsMax } = media

const ModalCustomNotes = ({
  isOpen,
  onRequestClose,
  productData,
  defaultQuantityValue = 1,
  defaultValue = '',
  mode = MODE_MODALCUSTOMERNOTES.DEFAULT,
  lang = LANGUAGES.TH,
  onAddToCart = (products) => {},
  onUpdateCustomNotes = (data) => {},
  onClickAddNewItem,
  defaultValueUseContainer,
  defaultValueModifiers = [],
  isShowIconWithButton = false,
  isLoading = false,
  onLineItemMaxQuantity = (maxQuantity) => {},
  hideZeroPrice = false,
  quantitySwitchColor = ThemeColor.secondary,
  titleJustify = 'center',
  productPriceColor = '#000000',
  warningTitle = '',
  showCustomNoteSkeleton = false,
  addToCartProcessing = false,
  disabledAddTocartBtn = false,
}) => {
  const { cart, isThaiLang } = useContext(AppContext)
  const { t } = useTranslation()
  const { getPropertyByLang } = useLanguage()

  const refScroll = useRef(null)
  const warningPopupTimer = useRef(null)
  const setWarningTextTimer = useRef(null)
  const resetModifiersTimer = useRef(null)

  const [initialedData, setInitialedData] = useState(false)
  const [note, setNote] = useState('')
  const [quantity, setQuantity] = useState(defaultQuantityValue)
  const [isMaxQuantityPerCart, setIsMaxQuantityPerCart] = useState(false)
  const [useContainer] = useState(true)
  const [selectedModifiers, setSelectedModifiers] = useState([])
  const [isTypingQuantity, setIsTypingQuantity] = useState(false)
  const [clickAmountAddQuantity, setClickAmountAddQuantity] = useState(0)
  const [promotionProductIDs, setPromotionProductIDs] = useState([])

  // Warning Msg
  const [isShowWarningText, setIsShowWarningText] = useState(false)
  const [warningText, setWarningText] = useState('')

  const promotionProduct = productData?.promotionProduct ? productData.promotionProduct : null
  const promotionProductTotalPriceSatangs = useMemo(() => {
    let promotionProductSelectedTotalSatangs = 0
    if (promotionProduct) {
      const { productGroupRewards } = promotionProduct
      productGroupRewards.forEach((groupReward) => {
        const { rewardType, rewardValue, products } = groupReward
        promotionProductIDs.forEach((promotionProductID) => {
          const productRewardTarget = products.find((product) => product.id === promotionProductID)
          if (productRewardTarget) {
            const rewardTotalSatangs = getRewardTotalSatangs({
              rewardType,
              rewardValue,
              totalPriceSatangs: productRewardTarget.totalPriceSatangs,
            })
            promotionProductSelectedTotalSatangs += rewardTotalSatangs
          }
        })
      })
    }
    return promotionProductSelectedTotalSatangs
  }, [promotionProduct, promotionProductIDs])

  // Sorting
  const _productData = useMemo(() => {
    const _productData = cloneObject(productData)
    if (_productData && _productData.modifierGroups?.length > 0) {
      _productData.modifierGroups.sort((a, b) => {
        return a.position - b.position
      })
    }
    return _productData
  }, [productData])

  const prepareSelectedModifiers = useCallback(
    (modifierGroups) => {
      const arrayModifierGroups = []
      try {
        if (modifierGroups?.length > 0) {
          modifierGroups.forEach(({ minimumQuantity, modifierGroup, modifierGroup: { modifiers } }) => {
            const hasSingleModifier = minimumQuantity >= 1 && modifiers.length === 1
            const isAutoSelect = minimumQuantity === modifiers.length || hasSingleModifier
            const modifierSelectedArray = defaultValueModifiers.filter(({ modifierID }) =>
              modifierGroup.modifiers.some(({ id }) => id === modifierID)
            )
            if (isAutoSelect) {
              modifiers.forEach((modifier) => {
                const { id } = modifier
                const findSelectedModifier = modifierSelectedArray.find(({ modifierID }) => modifierID === id)
                if (!findSelectedModifier) {
                  if (hasSingleModifier) {
                    modifierSelectedArray.push({ ...modifier, modifierID: id, quantity: minimumQuantity })
                  } else {
                    modifierSelectedArray.push({ ...modifier, modifierID: id, quantity: 1 })
                  }
                }
              })
            }
            arrayModifierGroups.push(modifierSelectedArray)
          })
        }
      } catch (error) {
        !isDevelopment && Sentry.captureException(new Error(`Error from prepare selected modifiers: ${error}`))
      }
      return arrayModifierGroups
    },
    [defaultValueModifiers]
  )

  useEffect(() => {
    if (isOpen || isLoading) {
      resetModifiersTimer.current && clearTimeout(resetModifiersTimer.current)
      setQuantity(defaultQuantityValue)
      setNote(defaultValue)
      const arrayModifierGroups = prepareSelectedModifiers(_productData?.modifierGroups ?? [])
      setSelectedModifiers(arrayModifierGroups)
      setInitialedData(true)
    } else {
      setInitialedData(false)
      resetModifiersTimer.current = setTimeout(() => {
        setSelectedModifiers([])
        setPromotionProductIDs([])
        setClickAmountAddQuantity(0)
      }, 300)
    }
    return () => {
      setInitialedData(false)
      setSelectedModifiers([])
      setClickAmountAddQuantity(0)
    }
  }, [isOpen, isLoading, _productData])

  useEffect(() => {
    let observeInterval
    if (isOpen) {
      observeInterval = setInterval(function () {
        if (refScroll.current) {
          disableBodyScroll(refScroll.current)
          clearInterval(observeInterval)
        }
      }, 100)

      if (mode === MODE_MODALCUSTOMERNOTES.DEFAULT) {
        setIsMaxQuantityPerCart(
          !isValidProductMaxQuantityToAddToCart({ product: _productData, quantity: defaultQuantityValue, cart })
        )
      } else {
        const limitQuantityMode = getProductLimitQuantityMode({ product: _productData })
        if (limitQuantityMode !== ProductLimitQuantityModeEnum.none) {
          setIsMaxQuantityPerCart(
            !(limitQuantityMode === ProductLimitQuantityModeEnum.maxQuantity
              ? defaultQuantityValue < _productData.maxQuantity
              : defaultQuantityValue < getProductInStockQuantity({ product: _productData }))
          )
        } else {
          setIsMaxQuantityPerCart(false)
        }
      }
    } else if (refScroll.current) {
      enableBodyScroll(refScroll.current)
    }

    return () => {
      clearInterval(observeInterval)
      clearTimeout(warningPopupTimer.current)
      setIsShowWarningText(false)
      setWarningText('')
    }
  }, [isOpen])

  let totalModifierPriceSatangs = 0
  if (_productData && _productData.modifierGroups?.length > 0 && selectedModifiers.length > 0) {
    totalModifierPriceSatangs = getTotalPriceSatangsSelectedModifiers(_productData.modifierGroups, selectedModifiers)
  }

  if (isLoading) {
    return (
      <Fragment>
        <ModalFromBottom isOpen={isOpen} onRequestClose={onRequestClose}>
          <ModalProductModifiersSkeleton customNoteSection={showCustomNoteSkeleton} />
        </ModalFromBottom>
      </Fragment>
    )
  }

  const labelBoxExcludedPromotion = isShowLabelExcludedFromProtmoion({ product: _productData, cart }) && (
    <LabelBox
      style={{
        marginTop: `${spacing.sm}px`,
        marginBottom: '10px',
        gridColumn: 'span 3',
      }}
      title={t('productExcludedPromotion')}
      text=""
      type="infoLight"
    />
  )

  const limitQuantityMode = getProductLimitQuantityMode({ product: _productData })
  const productQuantityCanAddToCart =
    mode === MODE_MODALCUSTOMERNOTES.DEFAULT
      ? getProductQuantityCanAddToCart({ product: _productData, cart })
      : limitQuantityMode === ProductLimitQuantityModeEnum.maxQuantity
      ? _productData.maxQuantity
      : getProductInStockQuantity({ product: _productData })

  const showProductPrice = !(_productData?.totalPriceSatangs === 0 && hideZeroPrice)

  return (
    <Fragment>
      <ModalFromBottom isOpen={isOpen} onRequestClose={onRequestClose}>
        <Form onSubmit={() => {}}>
          {_productData && (
            <>
              <ProductTitle justify={titleJustify}>
                {titleJustify === 'center' ? (
                  <Fonts.BaseTitleBold style={{ textAlign: 'center' }}>
                    {isThaiLang ? _productData.nameTh : _productData.nameEn}{' '}
                    {showProductPrice && ` ฿${displayPrice(_productData?.totalPriceSatangs / 100)}`}
                  </Fonts.BaseTitleBold>
                ) : (
                  <>
                    <Fonts.BaseTitleBold style={{ textAlign: 'left' }}>
                      {isThaiLang ? _productData.nameTh : _productData.nameEn}
                    </Fonts.BaseTitleBold>
                    {showProductPrice && (
                      <ProductPrice color={productPriceColor}>{` ฿${displayPrice(
                        _productData?.totalPriceSatangs / 100
                      )}`}</ProductPrice>
                    )}
                  </>
                )}
              </ProductTitle>
            </>
          )}
          {warningTitle && <WarningBox title={warningTitle} text="" type="warning" />}
          {promotionProduct && promotionProduct.productGroupRewards.length > 0 && (
            <CheckBoxPromotionProduct
              promotionProduct={promotionProduct}
              selectedProductIDs={promotionProductIDs}
              onChange={(productIDs) => setPromotionProductIDs(productIDs)}
            />
          )}

          <ScrollingContent id="scroll-modal-product-modifiers" ref={refScroll}>
            {labelBoxExcludedPromotion}
            {initialedData &&
              _productData &&
              _productData.modifierGroups?.map((modifierGroup, modifierGroupIndex) => {
                const { minimumQuantity, maximumQuantity } = modifierGroup

                let totalSelectedModifiersQuantity = 0
                selectedModifiers[modifierGroupIndex]?.forEach((_selectedModifier) => {
                  totalSelectedModifiersQuantity += _selectedModifier.quantity
                })
                const _isSelectedModifiersFitGroup = totalSelectedModifiersQuantity === maximumQuantity

                const _isModifierGroupRadioMode = isModifierGroupRadioMode(modifierGroup)

                const {
                  nameEn: groupNameEn,
                  nameTh: groupNameTh,
                  modifiers,
                  multipleQuantity,
                } = modifierGroup.modifierGroup

                const isAllModifiersInactive = !modifiers.some((modifier) => modifier.active)

                return (
                  <Element key={modifierGroupIndex} name={`modifiergroup-${modifierGroup.id}`} className="modifierList">
                    <ModifierGroups hide={isAllModifiersInactive}>
                      <ModifierGroup>
                        <ModifierGroupTitle>{isThaiLang ? groupNameTh : groupNameEn}</ModifierGroupTitle>
                        {!minimumQuantity ? (
                          <Fonts.SmallTitleRegular>{t('modifierOptionalText')}</Fonts.SmallTitleRegular>
                        ) : null}
                        <ModifierGroupDes>
                          {getTextMinAndMaxModifierGroup(minimumQuantity, maximumQuantity)}
                        </ModifierGroupDes>

                        <Modifiers>
                          {modifiers.map((modifier, itemIndex) => {
                            const { nameEn, nameTh, images, priceSatangs, active } = modifier

                            const imagePath =
                              images.length > 0
                                ? images[0].resizableURL.replace('{width}', '240').replace('{height}', '240')
                                : ''

                            const selectedModfier = selectedModifiers[modifierGroupIndex]?.find(
                              (_selectedModifier) => _selectedModifier.modifierID === modifier.id
                            )

                            const isSelectedModifier = selectedModfier ? true : false
                            const quantity = selectedModfier?.quantity ?? 0

                            const inActive =
                              !active || modifier.invalids.indexOf(InvalidModifierEnum.Inactive) !== -1 ? true : false
                            const isOutOfStock =
                              modifier.invalids.indexOf(InvalidModifierEnum.OutOfStock) !== -1 ? true : false

                            const isDisabledModifier =
                              !_isModifierGroupRadioMode && _isSelectedModifiersFitGroup && !isSelectedModifier

                            const isQuantityMode = multipleQuantity && !_isModifierGroupRadioMode

                            return (
                              <Modifier
                                hide={inActive}
                                key={itemIndex}
                                isSelected={isSelectedModifier}
                                onClick={(e) => {
                                  e.preventDefault()
                                  e.stopPropagation()

                                  if (isQuantityMode || isOutOfStock) {
                                    return false
                                  }

                                  if (isDisabledModifier) {
                                    const warningMsg = t('modifierWarningTextSelectMaximum', {
                                      modifierGroupName: isThaiLang ? groupNameTh : groupNameEn,
                                      max: maximumQuantity,
                                    })
                                    setWarningText(warningMsg)
                                    handleShowWarningText()
                                    return false
                                  }
                                  toggleModifier(modifier.id, modifierGroupIndex)
                                }}
                                data-testid={nameEn.toLowerCase()}
                              >
                                <ModifierThumbnail
                                  disabled={isDisabledModifier && !isOutOfStock}
                                  style={
                                    imagePath
                                      ? {
                                          backgroundImage: `url('${imagePath}')`,
                                        }
                                      : {}
                                  }
                                >
                                  {isOutOfStock && (
                                    <OutOfStockFilm>
                                      <Fonts.BaseTitleBold>สินค้าหมด</Fonts.BaseTitleBold>
                                    </OutOfStockFilm>
                                  )}
                                </ModifierThumbnail>
                                <ModifierName disabled={isDisabledModifier || isOutOfStock}>
                                  {isThaiLang ? nameTh : nameEn}
                                </ModifierName>
                                {priceSatangs > 0 && (
                                  <ModifierPrice disabled={isDisabledModifier || isOutOfStock}>
                                    + ฿{displayPrice(priceSatangs / 100)}
                                  </ModifierPrice>
                                )}
                                {isQuantityMode && (
                                  <QuantitySwitchModifierWrapper
                                    isOutOfStock={isOutOfStock}
                                    disabled={isDisabledModifier || isOutOfStock}
                                  >
                                    <QuantitySwitch
                                      sm
                                      green
                                      maxValue={
                                        maximumQuantity !== null
                                          ? maximumQuantity - totalSelectedModifiersQuantity + quantity
                                          : null
                                      }
                                      onNoticeOverMaxValue={() => {
                                        const warningMsg = t('modifierWarningTextSelectMaximum', {
                                          modifierGroupName: isThaiLang ? groupNameTh : groupNameEn,
                                          max: maximumQuantity,
                                        })
                                        setWarningText(warningMsg)
                                        handleShowWarningText()
                                      }}
                                      quantity={quantity}
                                      onUpdate={(val) => {
                                        if ((quantity === 0 && val === 1) || val === 0) {
                                          toggleModifier(modifier.id, modifierGroupIndex)
                                        } else {
                                          updateModifierQuantity(modifier.id, modifierGroupIndex, val)
                                        }
                                      }}
                                      color={quantitySwitchColor}
                                    />
                                  </QuantitySwitchModifierWrapper>
                                )}
                              </Modifier>
                            )
                          })}
                        </Modifiers>
                      </ModifierGroup>
                    </ModifierGroups>
                  </Element>
                )
              })}

            {_productData && !_productData.skipCustomNotesScreen && (
              <Fragment>
                <Title>{t('modaCustomNotesTitle')}</Title>
                <Text>
                  {getPropertyByLang({
                    en: _productData?.customNotesLabelEn ? _productData.customNotesLabelEn : '',
                    th: _productData?.customNotesLabelTh ? _productData.customNotesLabelTh : '',
                  })}
                </Text>
                <Form.Textarea
                  rows="3"
                  placeholder={t('modaCustomNotesPlaceholder')}
                  notFloatPlaceholder
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  onBlur={triggerScrollForIssueIphoneX}
                />
              </Fragment>
            )}
          </ScrollingContent>

          <Footer>
            <Fragment>
              <QuantitySwitchWrapper>
                <QuantitySwitch
                  green
                  xlg
                  isTypable
                  minValue={1}
                  maxValue={productQuantityCanAddToCart}
                  disabledIncreasing={isMaxQuantityPerCart}
                  quantity={quantity}
                  onFocus={() => setIsTypingQuantity(true)}
                  onBlur={() => setIsTypingQuantity(false)}
                  onClickIncreaseQuantity={() => {
                    setClickAmountAddQuantity(clickAmountAddQuantity + 1)
                  }}
                  onUpdate={(val) => {
                    let isValidMaxQuantity = true
                    if (mode === MODE_MODALCUSTOMERNOTES.DEFAULT) {
                      isValidMaxQuantity = isValidProductMaxQuantityToAddToCart({
                        product: _productData,
                        quantity: val,
                        cart,
                      })
                    } else {
                      const limitQuantityMode = getProductLimitQuantityMode({ product: _productData })
                      if (limitQuantityMode !== ProductLimitQuantityModeEnum.none) {
                        isValidMaxQuantity =
                          limitQuantityMode === ProductLimitQuantityModeEnum.maxQuantity
                            ? val < _productData.maxQuantity
                            : val < getProductInStockQuantity({ product: _productData })
                      }
                    }
                    setIsMaxQuantityPerCart(!isValidMaxQuantity)
                    setQuantity(val)

                    if (!isValidMaxQuantity) {
                      const limitQuantityMode = getProductLimitQuantityMode({ product: _productData })
                      if (limitQuantityMode === ProductLimitQuantityModeEnum.stock) {
                        onLineItemMaxQuantity(getProductInStockQuantity({ product: _productData }))
                      } else if (limitQuantityMode === ProductLimitQuantityModeEnum.maxQuantity) {
                        onLineItemMaxQuantity(_productData.maxQuantity)
                      }
                    }
                  }}
                  color={quantitySwitchColor}
                />
              </QuantitySwitchWrapper>

              {mode === MODE_MODALCUSTOMERNOTES.DEFAULT && (
                <Button
                  md2
                  minWidth="150px"
                  enableFastClick={false}
                  isProcessing={addToCartProcessing}
                  disabled={disabledAddTocartBtn}
                  onClick={() => {
                    if (!isTypingQuantity) {
                      handleAddNewItemToCart(quantity)
                    }
                  }}
                  data-testid="add-product-to-cart-btn"
                >
                  <NowrapText mediumText>
                    {t('btnAddToBasket')}
                    {showProductPrice && _productData && (
                      <>
                        <br />฿
                        {displayPriceBothNoneAnd2Digit(
                          ((_productData?.totalPriceSatangs +
                            totalModifierPriceSatangs +
                            promotionProductTotalPriceSatangs) /
                            100) *
                            quantity
                        )}
                      </>
                    )}
                  </NowrapText>
                </Button>
              )}

              {mode === MODE_MODALCUSTOMERNOTES.EDITONLYNOTE && (
                <Button
                  md2
                  minWidth="150px"
                  style={{ fontSize: isShowIconWithButton ? 14 : 16 }}
                  onClick={() => {
                    if (!isTypingQuantity) {
                      handleUpdateItem(quantity)
                    }
                  }}
                  data-testid="update-product-modifier-btn"
                >
                  <NowrapText mediumText>
                    {isShowIconWithButton && <Icon name="edit-outline" />}
                    {t('btnUpdateBasket')}
                    {showProductPrice && _productData && (
                      <>
                        <br />฿
                        {displayPriceBothNoneAnd2Digit(
                          ((_productData?.totalPriceSatangs +
                            totalModifierPriceSatangs +
                            promotionProductTotalPriceSatangs) /
                            100) *
                            quantity
                        )}
                      </>
                    )}
                  </NowrapText>
                </Button>
              )}
            </Fragment>
          </Footer>

          {clickAmountAddQuantity >= 10 && (
            <InfoCustomerCanTypeQuantity>
              <Icon name="information" />
              {t('canTypeQuantity')}
            </InfoCustomerCanTypeQuantity>
          )}

          <LabelAddressWarningWrapper show={isShowWarningText}>
            <Container>
              <Label type="black" hideIcon center text={warningText} />
            </Container>
          </LabelAddressWarningWrapper>
        </Form>
      </ModalFromBottom>
    </Fragment>
  )

  function handleShowWarningText() {
    setIsShowWarningText(true)
    clearTimeout(warningPopupTimer.current)
    warningPopupTimer.current = setTimeout(() => {
      setIsShowWarningText(false)
      clearTimeout(setWarningTextTimer.current)
      setWarningTextTimer.current = setTimeout(() => {
        setWarningText('')
      }, 300)
    }, 5000)
  }

  function getErrorMinMaxModifierGroup({ selectedModifiersTarget, modifierGroup, min, max }) {
    const { nameTh, nameEn } = modifierGroup

    if (min === null && max === null) {
      return false
    }

    // Case Minimum
    let totalSelectedModifiersQuantity = 0
    selectedModifiersTarget?.forEach((_selectedModifier) => {
      totalSelectedModifiersQuantity += _selectedModifier.quantity
    })
    if (min !== null && totalSelectedModifiersQuantity < min) {
      return t('modifierWarningTextSelectAtleast', {
        modifierGroupName: isThaiLang ? nameTh : nameEn,
        min,
      })
    }
  }

  function scrollToModifierGroup(id) {
    scroller.scrollTo(`modifiergroup-${id}`, {
      containerId: 'scroll-modal-product-modifiers',
      duration: 600,
      smooth: true,
      offset: 0,
    })
  }

  function getErrorInValidSelectedModifiers() {
    const { modifierGroups } = _productData
    let errorMsg = false
    let modifierGroupIDError = null

    for (let i = 0; i < modifierGroups?.length; i++) {
      const { minimumQuantity, maximumQuantity, modifierGroup, id } = modifierGroups[i]

      const isAllModifiersInactive = !modifierGroup.modifiers.some((modifier) => modifier.active)

      if (!isAllModifiersInactive) {
        errorMsg = getErrorMinMaxModifierGroup({
          selectedModifiersTarget: selectedModifiers[i],
          modifierGroup,
          min: minimumQuantity,
          max: maximumQuantity,
        })

        if (errorMsg) {
          modifierGroupIDError = id
          break
        }
      }
    }

    return {
      errorMsg,
      modifierGroupID: modifierGroupIDError,
    }
  }

  function getTextMinAndMaxModifierGroup(min, max) {
    if ((min === null && max === null) || (min === 1 && max === 1) || (min === null && max === 1)) {
      return ''
    } else if (min !== null && max === null) {
      return t('modifierOptionTextAtleast', { min })
    } else if (min !== null && max !== null) {
      if (min === max) {
        return t('modifierOptionText', { max })
      } else {
        return t('modifierOptionTextRange', { min, max })
      }
    } else if (min === null && max !== null) {
      return t('modifierOptionTextMaximum', { max })
    }
  }

  function getTotalPriceSatangsSelectedModifiers(modifierGroups, selectedModifiers) {
    let totalPriceSatangs = 0
    modifierGroups.forEach((modifierGroup, modifierGroupIndex) => {
      modifierGroup.modifierGroup.modifiers.forEach((modifier) => {
        const modifierTarget = selectedModifiers[modifierGroupIndex]?.find(
          (_modifier) => _modifier.modifierID === modifier.id
        )
        if (modifierTarget) {
          totalPriceSatangs += modifierTarget.quantity * modifier.priceSatangs
        }
      })
    })

    return totalPriceSatangs
  }

  function isModifierGroupRadioMode(modifierGroupTarget) {
    const { minimumQuantity, maximumQuantity } = modifierGroupTarget
    return (minimumQuantity <= 1 || minimumQuantity === null) && maximumQuantity === 1
  }

  function updateModifierQuantity(modifierID, modifierGroupIndex, quantity) {
    let _selectedModifiers = cloneObject(selectedModifiers)
    const targetModifier = _selectedModifiers[modifierGroupIndex]?.find(
      (_selectedModifier) => _selectedModifier.modifierID === modifierID
    )
    if (targetModifier) {
      targetModifier.quantity = quantity
    }
    setSelectedModifiers(_selectedModifiers)
  }

  function toggleModifier(modifierID, modifierGroupIndex) {
    const modifierGroupTarget = _productData.modifierGroups[modifierGroupIndex]
    const _isModifierGroupRadioMode = isModifierGroupRadioMode(modifierGroupTarget)
    let _selectedModifiers = cloneObject(selectedModifiers)
    const modifierData =
      modifierGroupTarget.modifierGroup.modifiers.find((modifier) => modifier.id === modifierID) || {}

    let _selectedModifiersGroup = _selectedModifiers[modifierGroupIndex]
    if (_selectedModifiersGroup) {
      if (_selectedModifiersGroup.some((_selectedModifier) => _selectedModifier.modifierID === modifierID)) {
        // Remove
        _selectedModifiersGroup = _selectedModifiersGroup.filter(
          (_selectedModifier) => _selectedModifier.modifierID !== modifierID
        )
      } else if (_isModifierGroupRadioMode) {
        _selectedModifiersGroup = [{ modifierID, quantity: 1, ...modifierData }]
      } else {
        // Add
        _selectedModifiersGroup.push({ modifierID, quantity: 1, ...modifierData })
      }
      _selectedModifiers[modifierGroupIndex] = _selectedModifiersGroup
    }

    setSelectedModifiers(_selectedModifiers)
  }

  function triggerScrollForIssueIphoneX() {
    setTimeout(() => {
      window.scrollTo(0, 0)
    }, 20)
  }

  function handleAddNewItemToCart(quantity) {
    // Check selectedModfiers is valid before addToCart
    const { errorMsg, modifierGroupID } = getErrorInValidSelectedModifiers()
    if (errorMsg) {
      setWarningText(errorMsg)
      handleShowWarningText()
      setTimeout(() => {
        scrollToModifierGroup(modifierGroupID)
      }, 300)
      return false
    }

    // flatten selected modifiers
    const modifiers = selectedModifiers.length > 0 ? selectedModifiers.reduce((a, b) => [...a, ...b]) : []

    const payloadAddToCart = [
      {
        item: _productData,
        quantity,
        customNotes: note,
        useContainer,
        modifiers,
      },
    ]

    // PromotionProduct
    if (promotionProduct && promotionProductIDs.length > 0) {
      promotionProductIDs.forEach((productID) => {
        promotionProduct.productGroupRewards.every((group) => {
          const promotionProductTarget = group.products.find((product) => product.id === productID)
          if (promotionProductTarget) {
            payloadAddToCart.push({
              item: promotionProductTarget,
              quantity,
              customNotes: '',
              useContainer,
              modifiers: [],
            })
            return false
          } else {
            return true
          }
        })
      })
    }

    onAddToCart(payloadAddToCart)
    onRequestClose()
  }

  function handleUpdateItem(quantity) {
    // flatten selected modifiers

    let modifiers = selectedModifiers.length > 0 ? selectedModifiers.reduce((a, b) => [...a, ...b]) : []

    modifiers = modifiers.map((modifier) => {
      let multipleQuantity = modifier.multipleQuantity
      if (!multipleQuantity) {
        _productData?.modifierGroups.forEach((modifierGroup) => {
          modifierGroup?.modifierGroup?.modifiers.forEach((originModifier) => {
            if (originModifier.id === modifier.id) {
              multipleQuantity = modifierGroup.modifierGroup.multipleQuantity
            }
          })
        })
      }
      return { ...modifier, multipleQuantity }
    })

    if (
      defaultValue !== note ||
      !isSameModifiers(defaultValueModifiers, modifiers) ||
      quantity !== defaultQuantityValue
    ) {
      // Check selectedModfiers is valid before addToCart
      const { errorMsg, modifierGroupID } = getErrorInValidSelectedModifiers()
      if (errorMsg) {
        setWarningText(errorMsg)
        handleShowWarningText()
        setTimeout(() => {
          scrollToModifierGroup(modifierGroupID)
        }, 300)
        return false
      }

      onUpdateCustomNotes({
        item: _productData,
        customNotes: note,
        modifiers,
        quantity,
      })
    }
    onRequestClose()
  }
}

ModalCustomNotes.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onRequestClose: PropTypes.func.isRequired,
  defaultValue: PropTypes.string,
  defaultValueUseContainer: PropTypes.bool,
  productData: PropTypes.object,
  mode: PropTypes.oneOf([MODE_MODALCUSTOMERNOTES.DEFAULT, MODE_MODALCUSTOMERNOTES.EDITONLYNOTE]),
  lang: PropTypes.string,
  onAddToCart: PropTypes.func,
  onUpdateCustomNotes: PropTypes.func,
  onClickAddNewItem: PropTypes.func,
  isShowIconWithButton: PropTypes.bool,
  onLineItemMaxQuantity: PropTypes.func,
  hideZeroPrice: PropTypes.bool,
  quantitySwitchColor: PropTypes.string,
}

export default ModalCustomNotes

const BounceAnimation = keyframes`${bounceIn}`

const ProductTitle = styled.div`
  position: relative;
  top: -20px;
  text-align: center;
  pointer-events: none;
  align-items: center;
  display: flex;
  column-gap: 10px;
  justify-content: ${({ justify }) => justify};
  padding-right: ${({ justify }) => (justify === 'space-between' ? '25px' : '15px')};
`

const ScrollingContent = styled.div`
  max-height: 400px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  position: sticky;
  bottom: 70px;

  @media (max-height: 720px) {
    max-height: 300px;
  }
`

const Title = styled(Fonts.Medium)`
  color: black;
`

const Text = styled.div`
  margin-bottom: 15px;
  font-size: 14px;
  color: ${colors.gray600};
  white-space: pre-wrap;
`

const Footer = styled.div`
  position: sticky;
  bottom: 0;
  padding-top: 10px;
  display: flex;
  align-items: center;
  justify-content: ${({ center }) => (center ? 'center' : 'space-between')};
  background: ${({ theme }) => theme.colors.white};
`

const QuantitySwitchWrapper = styled.div`
  margin-right: 10px;
`

const QuantitySwitchModifierWrapper = styled.div`
  margin-top: ${spacing.xs}px;

  ${({ disabled }) =>
    disabled
      ? `
    opacity: .5;
  `
      : ''}

  ${({ isOutOfStock }) =>
    isOutOfStock
      ? `
    pointer-events: none;
  `
      : ''}
`

// Modifiers
const ModifierGroups = styled.div`
  ${({ hide }) =>
    hide
      ? `
    display: none;
  `
      : ''}
`

const ModifierGroup = styled.div``
const ModifierGroupTitle = styled(Fonts.Medium)``
const ModifierGroupDes = styled(Fonts.SmallTitleRegular)`
  color: ${(props) => props.theme.colors.darkGrey};
`

const Modifiers = styled.div`
  margin: ${spacing.md}px 0;
  display: flex;
  flex-wrap: wrap;
`

const Modifier = styled.div`
  margin-bottom: ${spacing.lg}px;
  width: 33.33%;
  min-width: 80px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  transition: opacity 0.3s;

  ${({ isSelected, theme }) =>
    isSelected
      ? `
    ${ModifierThumbnail} {
      box-shadow: 0 0 0 3px ${theme.colors.success};

      &::after {
        display: flex;
      }
    }
    `
      : ''}

  ${({ hide }) =>
    hide
      ? `
    display: none;
  `
      : ''}
`

const ModifierThumbnail = styled.div`
  margin-bottom: ${spacing.sm}px;
  position: relative;
  width: 80%;
  max-width: 85px;
  aspect-ratio: 1/1;
  border-radius: 50%;
  box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.baseGrey};
  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;
  transition: box-shadow 0.3s;
  background-image: image-set(
    url(${process.env.PUBLIC_URL}/assets/generic/cart/placeholder-modifier.png) 1x,
    url(${process.env.PUBLIC_URL}/assets/generic/cart/placeholder-modifier@2x.png) 2x,
    url(${process.env.PUBLIC_URL}/assets/generic/cart/placeholder-modifier@3x.png) 3x
  );

  &::after {
    content: '\\e913';
    font-family: 'icons-indiedish' !important;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 25px;
    height: 25px;
    border-radius: 50%;
    overflow: hidden;
    background-color: ${({ theme }) => theme.colors.success};
    position: absolute;
    top: 6px;
    right: -6px;
    color: white;
    font-size: 11px;
    animation: ${BounceAnimation} 0.6s linear forwards;
    display: none;
  }

  ${({ disabled }) =>
    disabled
      ? `
    opacity: .5;
  `
      : ''}
`

const OutOfStockFilm = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  border-radius: 50%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
`

const ModifierName = styled(Fonts.SmallBold)`
  flex: 1;
  margin-bottom: 2px;
  color: ${({ theme }) => theme.colors.base};
  text-align: center;

  ${({ disabled }) =>
    disabled
      ? `
    opacity: .5;
  `
      : ''}
`

const ModifierPrice = styled(Fonts.Small)`
  color: ${(props) => props.theme.colors.base};

  ${({ disabled }) =>
    disabled
      ? `
    opacity: .5;
  `
      : ''}
`

const LabelAddressWarningWrapper = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 140px;
  z-index: 150;
  transition: 0.3s;
  opacity: 0;
  transform: translate3d(0, 100%, 0);
  visibility: hidden;

  ${({ show }) =>
    show
      ? `
    opacity: 1;
    visibility: visible;
    transform: translate3d(0, 0, 0);
    `
      : ''}
`

const NowrapText = styled.div`
  white-space: nowrap;

  ${({ mediumText }) =>
    mediumText
      ? `
    font-size: 16px;
  `
      : ''}

  ${({ smallText }) =>
    smallText
      ? `
    font-size: 14px;
  `
      : ''}

@media (${screenXsMax}) {
    font-size: 12px;
  }
`

const Button = styled(BaseButton)`
  @media (${screenXsMax}) {
    border-radius: 25px;
    min-width: 110px !important;
    min-height: 45px;
    height: 45px;
  }
`

const InfoCustomerCanTypeQuantity = styled.div`
  margin-top: 20px;
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.infoDark};
  font-size: 13px;

  i {
    margin-right: 6px;
  }
`

const ProductPrice = styled(Fonts.BaseTitleBold)`
  color: ${({ color }) => color};
  flex-shrink: 0;
  align-self: self-start;
`

const WarningBox = styled(LabelBox)`
  margin-top: -8px;
  margin-bottom: 8px;
`
